(() => {
  const ROOT_SELECTOR = '[data-nyxel-pdp-main]';
  const desktopMedia = window.matchMedia('(min-width: 1001px)');
  const OPTION_ALIASES = {
    color: ['color', 'colour'],
    size: ['size'],
    helmetType: ['helmet type', 'model']
  };

  function normalize(value) {
    return String(value || '').trim().toLowerCase();
  }

  function findOptionPosition(optionNames, aliases) {
    const index = optionNames.findIndex((name) => aliases.includes(normalize(name)));
    return index === -1 ? null : index + 1;
  }

  function formatMoney(cents) {
    const format = window.theme?.currencyCodeEnabled
      ? window.theme.moneyWithCurrencyFormat
      : window.theme?.moneyFormat;
    const formatString = format || '£{{amount}}';
    const placeholder = formatString.match(/\{\{\s*(\w+)\s*\}\}/);

    if (!placeholder) return formatString;

    const formatWithDelimiters = (value, precision = 2, thousands = ',', decimal = '.') => {
      const number = Number(value);
      if (!Number.isFinite(number)) return '0';

      const parts = (number / 100).toFixed(precision).split('.');
      const amount = parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thousands}`);
      return parts[1] ? `${amount}${decimal}${parts[1]}` : amount;
    };

    const formattedAmounts = {
      amount: formatWithDelimiters(cents),
      amount_no_decimals: formatWithDelimiters(cents, 0),
      amount_with_comma_separator: formatWithDelimiters(cents, 2, '.', ','),
      amount_no_decimals_with_comma_separator: formatWithDelimiters(cents, 0, '.', ','),
      amount_with_apostrophe_separator: formatWithDelimiters(cents, 2, "'", '.')
    };
    const formattedAmount = formattedAmounts[placeholder[1]];

    return formattedAmount === undefined
      ? formatString
      : formatString.replace(placeholder[0], formattedAmount);
  }

  class NyxelVariantAvailability {
    constructor(root) {
      this.root = root;
      this.form = root.querySelector('.ProductForm');
      this.productJson = this.form?.querySelector('[data-product-json]');

      if (!this.form || !this.productJson) return;

      try {
        this.product = JSON.parse(this.productJson.textContent).product;
      } catch (error) {
        console.error('Unable to read Nyxel variant availability.', error);
        return;
      }

      if (!this.product?.variants?.length || !this.product?.options?.length) return;

      this.positions = {
        color: findOptionPosition(this.product.options, OPTION_ALIASES.color),
        size: findOptionPosition(this.product.options, OPTION_ALIASES.size),
        helmetType: findOptionPosition(this.product.options, OPTION_ALIASES.helmetType)
      };

      if (Object.values(this.positions).some((position) => position === null)) return;

      this.availableVariants = this.product.variants.filter((variant) => variant.available);
      this.optionInputs = Array.from(
        this.form.querySelectorAll('.ProductForm__Option input[type="radio"][data-option-position]')
      );
      this.button = this.form.querySelector('.ProductForm__AddToCart');
      this.mobileFlowEnabled = root.dataset.nyxelMobilePurchaseFlow === 'true';
      this.requiredPosition = null;
      this.focusTimer = null;
      this.guideCorrectionTimer = null;
      this.handleButtonClick = this.handleButtonClick.bind(this);
      this.handleFormSubmit = this.handleFormSubmit.bind(this);
      this.handleMediaChange = this.handleMediaChange.bind(this);
      this.handleVariantChanged = this.handleVariantChanged.bind(this);

      this.decorateOptions();
      this.form.addEventListener('change', (event) => this.handleOptionChange(event));
      this.button?.addEventListener('click', this.handleButtonClick, true);
      this.form.addEventListener('submit', this.handleFormSubmit, true);
      this.root.addEventListener('variant:changed', this.handleVariantChanged);
      desktopMedia.addEventListener('change', this.handleMediaChange);
      this.sync();
      this.root.dataset.nyxelAvailabilityReady = 'true';
    }

    inputsAt(position) {
      return this.optionInputs.filter(
        (input) => Number(input.dataset.optionPosition) === position
      );
    }

    selectedValue(position) {
      return this.inputsAt(position).find((input) => input.checked && !input.disabled)?.value || null;
    }

    clearSelection(position) {
      this.inputsAt(position).forEach((input) => {
        input.checked = false;
      });
    }

    decorateOptions() {
      const soldOutLabel = this.root.dataset.nyxelSoldOutLabel || 'Sold out';

      this.optionInputs.forEach((input) => {
        const position = Number(input.dataset.optionPosition);
        const label = this.form.querySelector(`label[for="${CSS.escape(input.id)}"]`);

        if (!label || position === this.positions.color) return;
        if (label.querySelector('.NyxelOptionAvailabilityLabel')) return;

        const status = document.createElement('span');
        status.className = 'NyxelOptionAvailabilityLabel';
        status.textContent = soldOutLabel;
        label.appendChild(status);
      });
    }

    variantMatches(variant, position, value) {
      return variant.options[position - 1] === value;
    }

    hasAvailableVariant(position, value, fixedSelections = []) {
      return this.availableVariants.some((variant) => {
        if (!this.variantMatches(variant, position, value)) return false;

        return fixedSelections.every(([fixedPosition, fixedValue]) =>
          this.variantMatches(variant, fixedPosition, fixedValue)
        );
      });
    }

    setInputAvailability(input, isAvailable, isAwaitingSelection = false) {
      const item = input.closest('.HorizontalList__Item');
      const label = this.form.querySelector(`label[for="${CSS.escape(input.id)}"]`);
      const isDisabled = isAwaitingSelection || !isAvailable;

      input.disabled = isDisabled;
      input.setAttribute('aria-disabled', String(isDisabled));
      item?.classList.toggle('is-nyxel-unavailable', !isAwaitingSelection && !isAvailable);
      item?.classList.toggle('is-nyxel-awaiting-selection', isAwaitingSelection);

      if (label) {
        label.setAttribute('aria-disabled', String(isDisabled));
      }
    }

    updatePosition(position, fixedSelections = []) {
      const isAwaitingSelection = !fixedSelections.every(([, value]) => value);

      this.inputsAt(position).forEach((input) => {
        const isAvailable = !isAwaitingSelection &&
          this.hasAvailableVariant(position, input.value, fixedSelections);

        this.setInputAvailability(input, isAvailable, isAwaitingSelection);
      });
    }

    clearIfUnavailable(position) {
      const selectedInput = this.inputsAt(position).find((input) => input.checked);

      if (!selectedInput?.disabled) return false;
      this.clearSelection(position);
      return true;
    }

    optionAt(position) {
      return this.inputsAt(position)[0]?.closest('.ProductForm__Option') || null;
    }

    selectedOptions() {
      return this.product.options.map((_, index) => this.selectedValue(index + 1));
    }

    selectedVariant() {
      const selectedOptions = this.selectedOptions();
      if (selectedOptions.some((value) => !value)) return null;

      return this.product.variants.find((variant) =>
        variant.options.every((value, index) => value === selectedOptions[index])
      ) || null;
    }

    updateVariantPrices(variant) {
      if (!variant || !Number.isFinite(Number(variant.price))) return;

      const floatingSection = document.querySelector('[id*="quick_add_to_cart_v2"]');
      const priceLists = new Set([
        ...this.root.querySelectorAll('.ProductMeta__PriceList:not(.has-bundle-product)'),
        ...(floatingSection?.querySelectorAll('.ProductMeta__PriceList') || [])
      ]);
      const hasCompareAtPrice =
        Number(variant.compare_at_price) > Number(variant.price);

      priceLists.forEach((priceList) => {
        const price = document.createElement('span');
        price.className = [
          'ProductMeta__Price',
          'product-price',
          'Price',
          hasCompareAtPrice ? 'Price--highlight' : '',
          'Text--subdued',
          'u-h4'
        ].filter(Boolean).join(' ');
        price.dataset.moneyConvertible = '';
        price.innerHTML = formatMoney(variant.price);

        if (!hasCompareAtPrice) {
          priceList.replaceChildren(price);
          priceList.style.display = '';
          return;
        }

        const compareAtPrice = document.createElement('span');
        compareAtPrice.className =
          'ProductMeta__Price product-price Price Price--compareAt Text--subdued u-h4';
        compareAtPrice.dataset.moneyConvertible = '';
        compareAtPrice.innerHTML = formatMoney(variant.compare_at_price);
        priceList.replaceChildren(compareAtPrice, price);
        priceList.style.display = '';
      });
    }

    emitPurchaseState(status, prompt = null, variant = null) {
      const state = {
        status,
        prompt,
        requiredPosition: this.requiredPosition,
        selectedOptions: this.selectedOptions(),
        variantId: variant?.id || null,
        price: variant?.price ?? null,
        compareAtPrice: variant?.compare_at_price ?? null
      };

      this.root.__nyxelPurchaseState = state;
      this.root.dispatchEvent(new CustomEvent('nyxel:purchase-state-changed', {
        bubbles: true,
        detail: state
      }));
    }

    clearOptionGuidance() {
      if (this.focusTimer !== null) {
        window.clearTimeout(this.focusTimer);
        this.focusTimer = null;
      }

      if (this.guideCorrectionTimer !== null) {
        window.clearTimeout(this.guideCorrectionTimer);
        this.guideCorrectionTimer = null;
      }

      this.form.querySelectorAll('.ProductForm__Option.is-nyxel-option-required').forEach((option) => {
        option.classList.remove('is-nyxel-option-required');
        option.removeAttribute('aria-invalid');
        option.removeAttribute('tabindex');
      });
    }

    guideToRequiredOption() {
      const option = this.optionAt(this.requiredPosition);
      if (!option) return;

      this.clearOptionGuidance();
      option.classList.add('is-nyxel-option-required');
      option.setAttribute('aria-invalid', 'true');
      option.setAttribute('tabindex', '-1');
      const requiredPosition = this.requiredPosition;
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const purchaseRail = option.closest('.Product__Info');

      if (desktopMedia.matches && purchaseRail && purchaseRail.scrollHeight > purchaseRail.clientHeight) {
        const optionRect = option.getBoundingClientRect();
        const railRect = purchaseRail.getBoundingClientRect();
        const optionTop = optionRect.top - railRect.top + purchaseRail.scrollTop;
        const centeredTop = optionTop - (purchaseRail.clientHeight - optionRect.height) / 2;
        const maxScrollTop = purchaseRail.scrollHeight - purchaseRail.clientHeight;

        purchaseRail.scrollTo({
          top: Math.max(0, Math.min(centeredTop, maxScrollTop)),
          behavior: reduceMotion ? 'auto' : 'smooth'
        });
      } else if (!desktopMedia.matches && this.mobileFlowEnabled) {
        const scrollTarget = this.requiredPosition === this.positions.helmetType
          ? this.optionAt(this.positions.color) || option
          : option;
        const rootStyles = window.getComputedStyle(this.root);
        const galleryOffset = Number.parseFloat(
          rootStyles.getPropertyValue('--nyxel-mobile-gallery-height')
        );
        const header = document.getElementById('shopify-section-header');
        const headerOffset = header ? Math.max(0, header.getBoundingClientRect().bottom) : 0;
        const scrollOffset = headerOffset +
          (Number.isFinite(galleryOffset) ? galleryOffset : 0) +
          16;

        window.scrollTo({
          top: Math.max(0, window.scrollY + scrollTarget.getBoundingClientRect().top - scrollOffset),
          behavior: reduceMotion ? 'auto' : 'smooth'
        });

        this.guideCorrectionTimer = window.setTimeout(() => {
          this.guideCorrectionTimer = null;
          if (this.requiredPosition !== requiredPosition) return;
          if (!option.classList.contains('is-nyxel-option-required')) return;

          const currentHeaderOffset = header
            ? Math.max(0, header.getBoundingClientRect().bottom)
            : 0;
          const stickyGallery = this.root.querySelector('.product-img-container--nyxel');
          const galleryRect = stickyGallery?.getBoundingClientRect();
          const isGallerySticky = stickyGallery &&
            window.getComputedStyle(stickyGallery).position === 'sticky';
          const visibleTop = Math.max(
            currentHeaderOffset,
            isGallerySticky && galleryRect ? galleryRect.bottom : 0
          ) + 16;
          const targetRect = scrollTarget.getBoundingClientRect();
          const requiredRect = option.getBoundingClientRect();
          const floatingSection = document.querySelector('[id*="quick_add_to_cart_v2"]');
          const floatingRect = floatingSection?.getBoundingClientRect();
          const visibleBottom = floatingRect && floatingRect.height > 0
            ? floatingRect.top - 16
            : window.innerHeight - 16;
          let correction = targetRect.top - visibleTop;
          const predictedRequiredBottom = requiredRect.bottom - correction;

          if (predictedRequiredBottom > visibleBottom) {
            correction += predictedRequiredBottom - visibleBottom;
          }

          if (Math.abs(correction) > 1) {
            window.scrollBy({
              top: correction,
              behavior: 'auto'
            });
          }
        }, reduceMotion ? 0 : 450);
      }

      this.focusTimer = window.setTimeout(() => {
        this.focusTimer = null;
        if (this.requiredPosition !== requiredPosition) return;
        if (!option.classList.contains('is-nyxel-option-required')) return;
        option.focus({ preventScroll: true });
      }, reduceMotion ? 0 : 500);
    }

    setIncompleteState(prompt, position) {
      const paymentButton = this.form.querySelector('.shopify-payment-button');

      this.root.classList.add('is-nyxel-selection-incomplete');
      this.requiredPosition = position;

      if (this.button) {
        const isInteractive = desktopMedia.matches || this.mobileFlowEnabled;
        const label = document.createElement('span');
        label.textContent = prompt;
        this.button.replaceChildren(label);
        this.button.disabled = !isInteractive;
        this.button.type = isInteractive ? 'button' : 'submit';
        this.button.removeAttribute('data-action');

        if (isInteractive) {
          this.button.removeAttribute('aria-disabled');
          this.button.setAttribute('data-nyxel-option-target', String(position));
        } else {
          this.button.setAttribute('aria-disabled', 'true');
          this.button.removeAttribute('data-nyxel-option-target');
        }
      }

      if (paymentButton) paymentButton.style.display = 'none';
      this.emitPurchaseState('incomplete', prompt);
    }

    setCompleteState() {
      const variant = this.selectedVariant();
      const isAvailable = Boolean(variant?.available);
      const label = isAvailable
        ? this.root.dataset.nyxelAddToCartLabel || 'Add to cart'
        : this.root.dataset.nyxelSoldOutLabel || 'Sold out';

      this.updateVariantPrices(variant);
      this.root.classList.remove('is-nyxel-selection-incomplete');
      this.requiredPosition = null;
      this.clearOptionGuidance();

      if (this.button) {
        const buttonLabel = document.createElement('span');
        buttonLabel.textContent = label;
        this.button.replaceChildren(buttonLabel);
        this.button.type = 'submit';
        this.button.removeAttribute('data-nyxel-option-target');
        this.button.disabled = !isAvailable;
        this.button.classList.toggle('Button--primary', isAvailable);
        this.button.classList.toggle('Button--secondary', !isAvailable);

        if (isAvailable) {
          this.button.setAttribute('data-action', 'add-to-cart');
          this.button.removeAttribute('aria-disabled');
        } else {
          this.button.removeAttribute('data-action');
          this.button.setAttribute('aria-disabled', 'true');
        }
      }

      this.emitPurchaseState(isAvailable ? 'available' : 'sold-out', label, variant);
    }

    sync() {
      const { color, size, helmetType } = this.positions;

      this.updatePosition(color);
      if (this.clearIfUnavailable(color)) {
        this.clearSelection(size);
        this.clearSelection(helmetType);
      }

      const selectedColor = this.selectedValue(color);
      this.updatePosition(size, [[color, selectedColor]]);
      if (this.clearIfUnavailable(size)) this.clearSelection(helmetType);

      const selectedSize = this.selectedValue(size);
      this.updatePosition(helmetType, [
        [color, selectedColor],
        [size, selectedSize]
      ]);
      this.clearIfUnavailable(helmetType);

      const selectedHelmetType = this.selectedValue(helmetType);

      if (!selectedColor) {
        this.setIncompleteState(this.root.dataset.nyxelChooseColorLabel || 'Choose color', color);
      } else if (!selectedSize) {
        this.setIncompleteState(this.root.dataset.nyxelChooseSizeLabel || 'Choose size', size);
      } else if (!selectedHelmetType) {
        this.setIncompleteState(
          this.root.dataset.nyxelChooseHelmetTypeLabel || 'Choose helmet type',
          helmetType
        );
      } else {
        this.setCompleteState();
      }
    }

    handleButtonClick(event) {
      if (!desktopMedia.matches && !this.mobileFlowEnabled) return;
      if (!this.root.classList.contains('is-nyxel-selection-incomplete')) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      this.guideToRequiredOption();
    }

    handleFormSubmit(event) {
      if (!desktopMedia.matches && !this.mobileFlowEnabled) return;
      if (!this.root.classList.contains('is-nyxel-selection-incomplete')) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      this.guideToRequiredOption();
    }

    handleOptionChange(event) {
      if (!event.target.matches('input[type="radio"][data-option-position]')) return;

      const position = Number(event.target.dataset.optionPosition);
      if (!Object.values(this.positions).includes(position)) return;

      this.clearOptionGuidance();
      window.requestAnimationFrame(() => this.sync());
    }

    handleMediaChange() {
      if (!this.root.isConnected) {
        desktopMedia.removeEventListener('change', this.handleMediaChange);
        return;
      }

      this.clearOptionGuidance();
      this.sync();
    }

    handleVariantChanged() {
      window.requestAnimationFrame(() => this.sync());
    }
  }

  function initialize(root = document) {
    root.querySelectorAll(ROOT_SELECTOR).forEach((pdpRoot) => {
      if (pdpRoot.dataset.nyxelAvailabilityReady === 'true') return;
      new NyxelVariantAvailability(pdpRoot);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(), { once: true });
  } else {
    initialize();
  }

  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
})();
